import { context, trace } from '@opentelemetry/api';
import { logger } from '@parrot-software/observability';

import type { PaymentMethodViewDto } from '@subscriptions/application/dtos';
import type { IPaymentMethodApiPort } from '@subscriptions/application/ports';
import { extractErrorAttributes } from '@subscriptions/application/utils';
import type { PaymentSessionMode } from '@subscriptions/domain/types/SubscriptionsConst';

const paymentLog = logger.forDomain('SUB', { subdomain: 'billing' });
const tracer = trace.getTracer('@parrot-software/observability');

/**
 * Use case for confirming a completed Stripe session and returning the resulting
 * payment method.
 *
 * Never logs `sessionId` / `providerId` / card digits (charter §5).
 *
 * @param port - Payment method API port
 * @returns Use case executor
 */
export const confirmSessionUseCase = (port: IPaymentMethodApiPort) => {
  const execute = async (
    blockUuid: string,
    sessionId: string,
    mode: PaymentSessionMode,
    isUpdate: boolean,
  ): Promise<PaymentMethodViewDto> => {
    const span = tracer.startSpan('SUB.confirmBlockPaymentSession', {
      attributes: { 'block.uuid': blockUuid, mode, isUpdate },
    });
    const ctx = trace.setSpan(context.active(), span);

    return context.with(ctx, async () => {
      try {
        const paymentMethod = await port.confirmSession(blockUuid, sessionId, mode, isUpdate);
        paymentLog.info('confirmBlockPaymentSession', {
          'block.uuid': blockUuid,
          'payment.uuid': paymentMethod.uuid,
          methodType: paymentMethod.methodType,
          mode,
          isUpdate,
        });
        return paymentMethod;
      } catch (error) {
        paymentLog.error('confirmBlockPaymentSession', { 'block.uuid': blockUuid, ...extractErrorAttributes(error) });
        throw error;
      } finally {
        span.end();
      }
    });
  };

  return { execute };
};
